import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { langPath } from '../utils/siteNav';

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const { t, language } = useLanguage();

  const faqs = t('faq.items') || [];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!Array.isArray(faqs) || faqs.length === 0) return null;

  return (
    <section id="faq" className="py-20 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">
            {t('faq.subtitle')}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-slate-800 mt-3 mb-4">
            {t('faq.title')}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {t('faq.description')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={faq.q}
              className="border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-amber-300"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-amber-50/50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="text-slate-800 font-medium pr-4 text-sm md:text-base">
                  {faq.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <p className="text-slate-500 text-sm mb-3">{t('faq.stillQuestions')}</p>
          <Link
            to={langPath(language, 'contact')}
            className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-full font-medium transition-colors"
            data-testid="faq-contact-btn"
          >
            <HelpCircle className="w-4 h-4" />
            {t('faq.contactUs')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
